import { useState } from "react";
import { MapPin, Phone, Mail, MessageCircle, Send } from "lucide-react";
import { useI18n } from "@/context/I18nContext";

export default function Contact() {
  const { t } = useI18n();
  const [form, setForm] = useState({ name: "", phone: "", plan: "1 Day Trial @ ₹84", message: "" });

  const submit = (e) => {
    e.preventDefault();
    const msg = `Hi Divya Dhara!\n\nName: ${form.name}\nPhone: ${form.phone}\nInterested in: ${form.plan}\n\n${form.message}`.trim();
    window.open(`https://wa.me/919819933676?text=${encodeURIComponent(msg)}`, "_blank", "noopener,noreferrer");
  };

  return (
    <section id="contact" className="dd-section bg-dd-page" data-testid="contact-section">
      <div className="dd-container">
        <div className="text-center mb-12 max-w-2xl mx-auto">
          <div className="dd-label text-dd-gold">{t.contact.eyebrow}</div>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl text-dd-ink mt-3">{t.contact.title}</h2>
          <div className="dd-ornament my-5"><span className="text-lg">✦</span></div>
        </div>

        <div className="grid md:grid-cols-5 gap-8 items-start">
          <form onSubmit={submit} className="md:col-span-3 bg-dd-ivory border border-dd-line rounded-2xl p-7 md:p-9 space-y-5" data-testid="contact-form">
            <h3 className="font-serif text-2xl text-dd-ink">{t.contact.form_heading}</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="dd-label text-dd-ink-muted block mb-2">{t.contact.name}</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full bg-white border border-dd-line rounded-lg px-4 py-3 outline-none focus:border-dd-orange focus:ring-1 focus:ring-dd-orange" data-testid="contact-name" />
              </div>
              <div>
                <label className="dd-label text-dd-ink-muted block mb-2">{t.contact.phone}</label>
                <input required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full bg-white border border-dd-line rounded-lg px-4 py-3 outline-none focus:border-dd-orange focus:ring-1 focus:ring-dd-orange" data-testid="contact-phone" />
              </div>
            </div>
            <div>
              <label className="dd-label text-dd-ink-muted block mb-2">{t.contact.plan}</label>
              <select value={form.plan} onChange={(e) => setForm({ ...form, plan: e.target.value })} className="w-full bg-white border border-dd-line rounded-lg px-4 py-3 outline-none focus:border-dd-orange" data-testid="contact-plan">
                <option>1 Day Trial @ ₹84</option>
                <option>Amrit Daily — ₹3,000/month</option>
                <option>Family Plan — ₹5,900/month</option>
                <option>Pure Cow Ghee — Price on Call</option>
                <option>Corporate / Bulk 5L+</option>
              </select>
            </div>
            <div>
              <label className="dd-label text-dd-ink-muted block mb-2">{t.contact.message}</label>
              <textarea rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="w-full bg-white border border-dd-line rounded-lg px-4 py-3 outline-none focus:border-dd-orange focus:ring-1 focus:ring-dd-orange" data-testid="contact-message" />
            </div>
            <button type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-b from-dd-orange to-dd-orange-dark hover:from-dd-orange-light hover:to-dd-orange text-white py-3.5 rounded-full font-semibold transition-all" data-testid="contact-submit">
              <Send className="w-4 h-4" /> {t.contact.send}
            </button>
          </form>

          <aside className="md:col-span-2 space-y-4">
            {[
              { icon: MapPin, title: t.contact.address_title, body: t.contact.address },
              { icon: Phone, title: t.contact.call_title, body: "+91 98199 33676  ·  +91 93211 03399", href: "tel:+919819933676" },
              { icon: MessageCircle, title: t.contact.whatsapp_title, body: t.contact.whatsapp_text, href: "https://wa.me/919819933676", accent: true },
              { icon: Mail, title: t.contact.email_title, body: "matrubhumigaurasbhandar@gmail.com", href: "mailto:matrubhumigaurasbhandar@gmail.com" },
            ].map((c, i) => (
              <a
                key={i}
                href={c.href || "#"}
                target={c.href?.startsWith("http") ? "_blank" : undefined}
                rel="noreferrer"
                className={`block rounded-xl border p-5 transition-all ${c.accent ? "bg-[#25D366] border-[#25D366] text-white hover:brightness-105" : "bg-dd-ivory border-dd-line text-dd-ink hover:border-dd-gold"}`}
              >
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${c.accent ? "bg-white/15" : "bg-dd-orange/10"}`}>
                  <c.icon className={`w-5 h-5 ${c.accent ? "text-white" : "text-dd-orange"}`} strokeWidth={1.75} />
                </div>
                <div className="dd-label mb-1 opacity-80">{c.title}</div>
                <div className="text-sm leading-relaxed">{c.body}</div>
              </a>
            ))}
          </aside>
        </div>

        {/* Google Maps embed */}
        <div className="mt-10 rounded-2xl overflow-hidden border border-dd-line shadow-md" data-testid="contact-maps">
          <iframe
            title="Matrubhumi Gauras Bhandar location"
            src="https://maps.google.com/maps?q=Matrubhumi%20Gauras%20Bhandar%20Nerul%20Navi%20Mumbai%20400706&t=&z=15&ie=UTF8&iwloc=&output=embed"
            width="100%"
            height="340"
            style={{ border: 0 }}
            loading="lazy"
            allowFullScreen
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </section>
  );
}
